import React, { ChangeEvent } from 'react';
import {
    CheckboxGroup, DatePicker, Infobox, Title, Loader,
} from 'ui-library';

import ButtonRadioElement from 'components/AtomicStructure/atoms/ButtonRadioElement';
import Error from 'components/AtomicStructure/atoms/Error';
import Input from 'components/AtomicStructure/atoms/Input';
import SelectElement from 'components/AtomicStructure/atoms/SelectElement';
import NewWrapper from 'components/AtomicStructure/layouts/NewWrapper';
import PageSection from 'components/PageSection/PageSection';
import Preloader from 'components/Preloader';
import ButtonsBlockRow from 'components/ButtonsBlockRow';

import { MailingAddress } from 'components/MailingAddress';
import { withMainLayout } from 'hocs';
import { compareDates } from 'utils/datetime';
import { isEmpty } from 'utils';

import './NewPersonalInformationPage.css';

import {
    validateInputLength,
    validateInputNumber,
    validateInputString,
    validateName,
    validateSelect,
} from 'validation/validation';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FIELD_LENGTH } from '../../../../ClientOverview/constants';
import useNewPersonalInformation from './useNewPersonalInformation';

const NewPersonalInformationPage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const usCitizenOption = [
        { label: t('clientDashboard.yes'), id: true },
        { label: t('clientDashboard.no'), id: false },
    ];

    const {
        isLoading,
        loadingUserData,
        error,
        data,
        optionGender,
        passData,
        errorData,
        cachedData,
        setData,
        setErrorData,
        onDateClick,
        optionCountry,
        optionMaritalStatus,
        optionLanguage,
        isJunior,
        handleSubmitMailingAddress,
        changeMobileMask,
        onChangeEmail,
        errorPage,
        isLoadingAPICall,
        goToAction,
    } = useNewPersonalInformation();

    const goBack = () => {
        navigate('/clients');
    };

    return (
        <NewWrapper
            stepNavBarActive={0}
            headTitle={t('onboardingFlow.customerOnboarding.title')}
        >
            <PageSection className="newStyle NewPersonalInformationPage">
                <div className="newStyleContent">
                    <Preloader isLoading={isLoading || loadingUserData} error={error}>
                        <div className="container">
                            <Title type={2}>
                                {t('onboardingFlow.personalPage.personalInformation')}
                            </Title>

                            <div className="desktop-col">
                                <div className="form">
                                    <ButtonRadioElement
                                        data-cy="gender"
                                        label={t('onboardingFlow.personalPage.gender')}
                                        value={data?.gender}
                                        nameData="gender"
                                        items={optionGender}
                                        passData={passData}
                                        error={errorData?.gender}
                                        required
                                    />
                                    <Input
                                        data-cy="firstName"
                                        label={t('onboardingFlow.personalPage.firstNames')}
                                        value={data?.firstName || ''}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            const val = e.target.value;

                                            setData({ ...data, firstName: val });
                                            setErrorData({
                                                ...errorData,
                                                firstName: validateName(val) || validateInputLength(val, FIELD_LENGTH.firstName),
                                            });
                                        }}
                                        disabled={!isEmpty(cachedData?.firstName)}
                                        error={errorData?.firstName}
                                        required
                                    />

                                    <Input
                                        data-cy="lastName"
                                        label={t('onboardingFlow.personalPage.surname')}
                                        value={data?.lastName || ''}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            const val = e.target.value;

                                            setData({ ...data, lastName: val });
                                            setErrorData({
                                                ...errorData,
                                                lastName: validateName(val) || validateInputLength(val, FIELD_LENGTH.lastName),
                                            });
                                        }}
                                        disabled={!isEmpty(cachedData?.lastName)}
                                        error={errorData?.lastName}
                                        required
                                    />

                                    <div>
                                        <DatePicker
                                            data-cy="dateOfBirth"
                                            label={t('onboardingFlow.personalPage.dateOfBirth')}
                                            value={data?.dateOfBirth}
                                            onChange={onDateClick}
                                            disabledDate={(current: any) => current
                                                && !compareDates(new Date(), current)}
                                            error={errorData?.dateOfBirth}
                                            format="DD.MM.YYYY"
                                            disabled={!isEmpty(cachedData?.dateOfBirth)}
                                            required
                                        />
                                    </div>

                                    <SelectElement
                                        data-cy="countryOfResidence"
                                        label={t('onboardingFlow.personalPage.countryOfResidence')}
                                        options={optionCountry}
                                        value={data?.countryOfResidence}
                                        onChange={(val: any) => {
                                            setData({ ...data, countryOfResidence: `${val}` });
                                            setErrorData({
                                                ...errorData,
                                                countryOfResidence: validateSelect(`${val}`),
                                            });
                                        }}
                                        error={errorData?.countryOfResidence}
                                        required
                                    />

                                    <div className="form-group-row">
                                        <Input
                                            data-cy="street"
                                            label={`${t('onboardingFlow.personalPage.strret')}/${t('onboardingFlow.personalPage.number')}`}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                const val = e.target.value;

                                                setData({ ...data, street: val });
                                                setErrorData({
                                                    ...errorData,
                                                    street: validateInputString(val) || validateInputLength(val, FIELD_LENGTH.street),
                                                });
                                            }}
                                            value={data?.street || ''}
                                            error={errorData?.street}
                                            required
                                        />
                                    </div>

                                    <div className="form-group-row">
                                        <Input
                                            data-cy="PLZ"
                                            label={t('onboardingFlow.personalPage.plz')}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                const val = e.target.value;

                                                setData({ ...data, PLZ: val });
                                                setErrorData({
                                                    ...errorData,
                                                    PLZ: validateInputNumber(val),
                                                });
                                            }}
                                            value={data?.PLZ || ''}
                                            error={errorData?.PLZ}
                                            required
                                        />

                                        <Input
                                            data-cy="ORT"
                                            label={t('onboardingFlow.personalPage.ort')}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                const val = e.target.value;

                                                setData({ ...data, ORT: val });
                                                setErrorData({
                                                    ...errorData,
                                                    ORT: validateInputString(val) || validateInputLength(val, FIELD_LENGTH.city),
                                                });
                                            }}
                                            value={data?.ORT || ''}
                                            error={errorData?.ORT}
                                            disabled={false}
                                            required
                                        />
                                    </div>

                                    <SelectElement
                                        data-cy="maritalStatus"
                                        label={t('onboardingFlow.personalPage.maritalStatus')}
                                        options={optionMaritalStatus}
                                        value={data?.maritalStatus}
                                        onChange={(val: any) => {
                                            setData({ ...data, maritalStatus: `${val}` });
                                            setErrorData({
                                                ...errorData,
                                                maritalStatus: validateSelect(`${val}`),
                                            });
                                        }}
                                        error={errorData?.maritalStatus}
                                        required
                                    />

                                    <SelectElement
                                        data-cy="countryOfNationality"
                                        label={t('onboardingFlow.personalPage.countryOfNationality')}
                                        value={data?.countryOfNationality}
                                        onChange={(val: any) => {
                                            setData({ ...data, countryOfNationality: `${val}` });
                                            setErrorData({
                                                ...errorData,
                                                countryOfNationality: validateSelect(`${val}`),
                                            });
                                        }}
                                        options={optionCountry}
                                        error={errorData?.countryOfNationality}
                                        required
                                    />

                                    <SelectElement
                                        data-cy="language"
                                        label={t('onboardingFlow.personalPage.language')}
                                        options={optionLanguage}
                                        onChange={(val: any) => {
                                            setData({ ...data, language: `${val}` });
                                            setErrorData({
                                                ...errorData,
                                                language: validateSelect(`${val}`),
                                            });
                                        }}
                                        value={data?.language}
                                        error={errorData?.language}
                                        required
                                    />

                                    <MailingAddress
                                        error={!!errorData?.mailRecipientAddress}
                                        required={isJunior}
                                        initData={data}
                                        onSubmit={handleSubmitMailingAddress}
                                        isCHOnly
                                    />

                                    <Input
                                        data-cy="primaryPhoneNumber"
                                        label={t('dynamicCRM.communicationMethods.primaryPhoneNumber')}
                                        type="tel"
                                        value={data?.primaryPhoneNumber || ''}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            changeMobileMask(e?.target?.value, 'primaryPhoneNumber', '+');
                                        }}
                                        error={errorData?.primaryPhoneNumber}
                                        required
                                    />

                                    <Input
                                        data-cy="primaryEmail"
                                        label={t('onboardingFlow.personalPage.email')}
                                        value={data?.primaryEmail || ''}
                                        onChange={onChangeEmail}
                                        error={errorData?.primaryEmail}
                                        required
                                    />

                                    <fieldset>
                                        <Title tagName="legend">
                                            {t('onboardingFlow.personalPage.taxInformation')}
                                        </Title>
                                        <div className="form">
                                            <SelectElement
                                                data-cy="countryOfBirth"
                                                label={t('onboardingFlow.personalPage.countryOfBirth')}
                                                value={data?.countryOfBirth}
                                                onChange={(val: any) => {
                                                    setData({ ...data, countryOfBirth: `${val}` });
                                                    setErrorData({
                                                        ...errorData,
                                                        countryOfBirth: validateSelect(`${val}`),
                                                    });
                                                }}
                                                options={optionCountry}
                                                error={errorData?.countryOfBirth}
                                            />

                                            <ButtonRadioElement
                                                data-cy="usCitizen"
                                                label={t('onboardingFlow.personalPage.usCitizen')}
                                                items={usCitizenOption}
                                                value={data?.usCitizen}
                                                nameData="usCitizen"
                                                passData={passData}
                                                error={errorData?.usCitizen}
                                                required
                                            />

                                            <ButtonRadioElement
                                                data-cy="hasUsResidence"
                                                label={t('dynamicCRM.taxSituation.hasUsResidence')}
                                                items={usCitizenOption}
                                                value={data?.hasUsResidence}
                                                nameData="hasUsResidence"
                                                passData={passData}
                                                error={errorData?.hasUsResidence}
                                                description={t('dynamicCRM.taxSituation.isUsPersonDesc')}
                                                required
                                            />

                                            <ButtonRadioElement
                                                data-cy="isUsPerson"
                                                label={t('dynamicCRM.taxSituation.isUsPerson')}
                                                items={usCitizenOption}
                                                value={data?.isUsPerson}
                                                nameData="isUsPerson"
                                                passData={passData}
                                                error={errorData?.isUsPerson}
                                                required
                                            />

                                            <fieldset>
                                                {data && (
                                                    <legend className="Form-control-label">
                                                        {`${t('dynamicCRM.taxSituation.customerConfirmation')} *`}
                                                    </legend>
                                                )}
                                                <CheckboxGroup
                                                    value={data.classedUsPerson ?? []}
                                                    options={[{ value: false, label: t('dynamicCRM.taxSituation.customerConfirmationNo'), position: 2 }, { value: true, label: t('dynamicCRM.taxSituation.customerConfirmationYes'), position: 1 }]}
                                                    error={errorData?.classedUsPerson}
                                                    size="big"
                                                    onChange={(item: any) => passData(item, 'classedUsPerson')}
                                                    defaultValue={[]}
                                                    isConditional
                                                    isInQuestionnaire
                                                    required
                                                />
                                            </fieldset>
                                        </div>
                                    </fieldset>
                                </div>
                            </div>
                            {isJunior && errorData?.mailRecipientAddress && (
                                <Infobox error showIcon={false} className="mb-16">
                                    {t('clientDetails.mailingAddress.juniorMailAddressRequiredMessage')}
                                </Infobox>
                            )}
                        </div>

                        <div className="JourneyFooter">
                            <div className="container">
                                {errorPage && <Error>{JSON.stringify(errorPage)}</Error>}

                                <ButtonsBlockRow
                                    additionalButton={{
                                        text: t('onboardingFlow.documetsPage.back'),
                                        onClick: () => goBack(),
                                        disabled: isLoadingAPICall,
                                        'data-cy': 'back',
                                    }}
                                    primaryButton={{
                                        text: isLoadingAPICall ? <Loader /> : t('onboardingFlow.clientsDataPage.continue'),
                                        onClick: () => goToAction(),
                                        disabled: isLoadingAPICall,
                                        'data-cy': 'continue',
                                    }}
                                />
                            </div>
                        </div>

                    </Preloader>
                </div>
            </PageSection>
        </NewWrapper>
    );
};

export default withMainLayout(NewPersonalInformationPage);
